import { calendarDateText, isGregorian, msToParts, partsToMs } from './calendar.js';
import { cmpStr, type StoryCtx } from './ctx.js';
import { childrenOf } from './tree.js';
import { TIME_UNITS, type Flag, type StoryNode, type TimeDisplay, type TimelineData, type TimeUnit } from './types.js';

/** Leerraum, den ein automatisch wachsender Zeitstrahl hinter seinem letzten Element lässt. */
export const TIMELINE_PADDING = 160;
/**
 * Kleinste Länge, auf die man einen Zeitraum durch Ziehen seiner Ränder bringt (Achseneinheiten). Die Daten erlauben kürzere
 * Zeiträume (etwa wenige Stunden an einem Tag, per Zeitwähler gesetzt); gezeichnet werden sie mit einer Mindestbreite.
 */
export const MIN_PERIOD_LENGTH = 4;
export const DEFAULT_PERIOD_LENGTH = 240;
/** Breite, die ein Ereignis (Zeitpunkt) beim Zusammenstoß mit anderen Elementen einnimmt: so viel Platz brauchen Symbol und Titel. */
export const EVENT_SPAN = 48;
/** Abstand der Spuren (Schienen) eines Zeitstrahls auf der Fläche */
export const LANE_HEIGHT = 104;

/**
 * Felder, in denen Szenen früher ihre Zeit trugen (lose bzw. nicht auf der Fläche). Heute steht sie in `StoryNode.time`; ältere Daten
 * werden beim Laden übernommen (`migrateSceneTimes`), ältere Aufrufe umgesetzt (`splitLegacyTime`).
 */
export const TIME_FIELDS = { event: 'time', periodStart: 'start', periodEnd: 'end' } as const;

export const eventsOf = (ctx: StoryCtx, timelineId: string): StoryNode[] => childrenOf(ctx, timelineId).filter((n) => n.kind === 'event');
export const periodsOf = (ctx: StoryCtx, timelineId: string): StoryNode[] => childrenOf(ctx, timelineId).filter((n) => n.kind === 'period');

/** Ist der Node ein am Zeitstrahl angedocktes Ereignis bzw. ein angedockter Zeitraum? */
export const isDocked = (ctx: StoryCtx, n: StoryNode): boolean => (n.kind === 'event' || n.kind === 'period') && !!n.parentId && ctx.nodes.get(n.parentId)?.kind === 'timeline';

/** Position eines Zeitstrahl-Elements (Ereignis: Zeitpunkt, Zeitraum: Beginn). */
export const positionOf = (n: StoryNode): number => (n.kind === 'event' ? (n.at ?? 0) : (n.from ?? 0));

/** Elemente eines Zeitstrahls in zeitlicher Reihenfolge (nach Position, bei Gleichstand nach Erstellung). */
export function orderedItems(ctx: StoryCtx, timelineId: string): StoryNode[] {
  return [...eventsOf(ctx, timelineId), ...periodsOf(ctx, timelineId)].sort((a, b) => positionOf(a) - positionOf(b) || cmpStr(a.createdAt, b.createdAt) || cmpStr(a.id, b.id));
}

/**
 * Zeitstrahl so verlängern, dass [min, max] samt Randabstand hineinpasst. Verkürzt nie. Haben Start und Ende eine Zeit (Datum oder eigene
 * Zählung), wandert sie mit der Verlängerung mit (lineare Fortsetzung), damit alle Elemente bei ihrer Zeit bleiben.
 */
export function growTimeline(t: TimelineData, min: number, max: number): TimelineData {
  return moveEdges(t, min < t.startPos ? Math.floor(min - TIMELINE_PADDING) : t.startPos, max > t.endPos ? Math.ceil(max + TIMELINE_PADDING) : t.endPos);
}

/**
 * Start und Ende eines Zeitstrahls an andere Stellen setzen, ohne die Zuordnung Stelle ↔ Zeit zu ändern: Die Zeit an Start und Ende
 * (Datum bzw. eigene Zählung) wird an den neuen Stellen abgelesen, alle Elemente behalten ihre Zeit. Ohne Zeitrechnung ändern sich
 * nur die Stellen.
 */
export function moveEdges(t: TimelineData, startPos: number, endPos: number): TimelineData {
  if (startPos === t.startPos && endPos === t.endPos) return t;
  if (t.reckoning) {
    const s = scaleOf(t);
    if (!s) return { ...t, startPos, endPos };
    return {
      ...t,
      startPos,
      endPos,
      reckoning: { ...t.reckoning, start: startPos !== t.startPos ? round6(valueAt(s, startPos)) : t.reckoning.start, end: endPos !== t.endPos ? round6(valueAt(s, endPos)) : t.reckoning.end },
    };
  }
  const startLabel = startPos !== t.startPos ? (dateAt(t, startPos) ?? t.startLabel) : t.startLabel;
  const endLabel = endPos !== t.endPos ? (dateAt(t, endPos) ?? t.endLabel) : t.endLabel;
  return { ...t, startPos, endPos, startLabel, endLabel };
}

/**
 * Prüft die Lage eines angedockten Ereignisses bzw. Zeitraums auf dem Zeitstrahl. Liefert eine Fehlermeldung oder null.
 * Zeiträume und Ereignisse dürfen sich überschneiden: parallele Elemente liegen auf eigenen Spuren (siehe `laneLayout`).
 */
export function checkTimelineItem(item: { kind: 'event' | 'period'; at?: number | null; from?: number | null; to?: number | null }): string | null {
  if (item.kind === 'event') {
    if (item.at === null || item.at === undefined || !Number.isFinite(item.at)) return 'Ein angedocktes Ereignis braucht eine Position auf der Zeitlinie.';
    return null;
  }
  const { from, to } = item;
  if (from === null || from === undefined || to === null || to === undefined || !Number.isFinite(from) || !Number.isFinite(to)) {
    return 'Ein angedockter Zeitraum braucht Anfang und Ende.';
  }
  if (to <= from) return 'Das Ende eines Zeitraums muss nach seinem Beginn liegen.';
  return null;
}

/** Belegter Bereich eines angedockten Elements auf der Achse (Ereignis: ein schmaler Streifen um den Zeitpunkt). */
export function spanOf(n: StoryNode): { start: number; end: number } {
  if (n.kind === 'event') return { start: (n.at ?? 0) - EVENT_SPAN / 2, end: (n.at ?? 0) + EVENT_SPAN / 2 };
  return { start: n.from ?? 0, end: n.to ?? n.from ?? 0 };
}

/** Belegter Bereich, wie er gezeigt wird (Zeitraffer): ein Ereignis ist so breit wie sonst auch */
function shownSpanOf(n: StoryNode, shown: (pos: number) => number): { start: number; end: number } {
  if (n.kind === 'event') return { start: shown(n.at ?? 0) - EVENT_SPAN / 2, end: shown(n.at ?? 0) + EVENT_SPAN / 2 };
  return { start: shown(n.from ?? 0), end: shown(n.to ?? n.from ?? 0) };
}

export interface LaneLayout {
  /** Spur (0 = oberste) je Element */
  lane: Map<string, number>;
  /** Anzahl der Spuren (mindestens 1) */
  count: number;
}

/**
 * Verteilt die angedockten Elemente eines Zeitstrahls auf Spuren: Ein Element, das ein anderes überschneidet, kommt auf die
 * nächste freie Spur, der Zeitstrahl bekommt eine Schiene mehr. Die Verteilung hängt nur von den Positionen und der Erstellungsreihenfolge ab.
 * `shown`: wo eine Stelle gezeigt wird (Zeitraffer, timeWarp.ts) – dann zählt die Überschneidung dort.
 */
export function laneLayout(ctx: StoryCtx, timelineId: string, shown?: (pos: number) => number): LaneLayout {
  const items = [...eventsOf(ctx, timelineId), ...periodsOf(ctx, timelineId)]
    .map((n) => ({ n, ...(shown ? shownSpanOf(n, shown) : spanOf(n)) }))
    .sort((a, b) => a.start - b.start || a.end - b.end || cmpStr(a.n.createdAt, b.n.createdAt) || cmpStr(a.n.id, b.n.id));
  const laneEnds: number[] = [];
  const lane = new Map<string, number>();
  for (const it of items) {
    let l = laneEnds.findIndex((end) => end <= it.start);
    if (l < 0) l = laneEnds.length;
    laneEnds[l] = it.end;
    lane.set(it.n.id, l);
  }
  return { lane, count: Math.max(1, laneEnds.length) };
}

/** Gesamter belegter Bereich der Elemente eines Zeitstrahls (null, wenn leer). */
export function itemsExtent(ctx: StoryCtx, timelineId: string): { min: number; max: number } | null {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const n of [...eventsOf(ctx, timelineId), ...periodsOf(ctx, timelineId)]) {
    const a = n.kind === 'event' ? n.at : n.from;
    const b = n.kind === 'event' ? n.at : n.to;
    if (a !== null && a < min) min = a;
    if (b !== null && b > max) max = b;
  }
  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null;
}

const STAMP = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/;
/** Millisekunden zu einem Datum `JJJJ-MM-TT[Thh:mm]` im Kalender der Geschichte (calendar.ts); null, wenn es keins ist. */
export const parseStamp = (s: string): number | null => {
  const m = STAMP.exec(s.trim());
  if (!m) return null;
  return partsToMs({ y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]), h: Number(m[4] ?? 0), mi: Number(m[5] ?? 0) });
};
const pad = (n: number): string => String(n).padStart(2, '0');
/** Millisekunden → Datum `JJJJ-MM-TTThh:mm` im Kalender der Geschichte (auf die Minute gerundet) */
export function stampText(ms: number): string {
  const p = msToParts(ms);
  return `${String(p.y).padStart(4, '0')}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}`;
}
const stampOf = stampText;
const round6 = (v: number): number => Math.round(v * 1e6) / 1e6;

// ---------------------------------------------------------------------------
// Zeitrechnung: Datum und Uhrzeit oder eigene Zählung (Minuten, Stunden, Tage, Jahre)
// ---------------------------------------------------------------------------

/** Minuten je Einheit einer eigenen Zählung (ein Jahr = 365,25 Tage) */
export const UNIT_MINUTES: Record<TimeUnit, number> = { minutes: 1, hours: 60, days: 1440, years: 525960 };
const DAYS_PER_YEAR = 365.25;

/** Uhrzeit des Nullpunkts in Minuten nach Mitternacht (ohne Nullpunkt: 0) */
const clockOf = (zeroMs: number | null | undefined): number => (zeroMs == null ? 0 : Math.round((((zeroMs / 60000) % 1440) + 1440) % 1440));

/**
 * Wert einer eigenen Zählung als Text, so wie er gespeichert und angezeigt wird: „Minute 45“, „Stunde 5, Minute 30“, „Tag 12, 06:00“,
 * „Jahr 340, Tag 120“. Minuten (Stunden), Uhrzeit (Tage) bzw. Tag im Jahr (Jahre) nur, wenn sie nicht null sind und die Zeitanzeige sie
 * zeigt. Hat der Nullpunkt eine Uhrzeit (`zeroMs`, z. B. 08:00), ist die Uhrzeit bei Tagen die echte Uhrzeit: Tag 0 beginnt um 08:00,
 * und die Uhrzeit steht dann immer dabei.
 */
export function formatCount(unit: TimeUnit, value: number, display: TimeDisplay = DEFAULT_TIME_DISPLAY, zeroMs?: number | null): string {
  if (unit === 'minutes') return `Minute ${Math.round(value)}`;
  if (unit === 'hours') {
    const total = Math.round(value * 60);
    const hour = Math.floor(total / 60);
    const rest = total - hour * 60;
    return `Stunde ${hour}${display.time && rest > 0 ? `, Minute ${rest}` : ''}`;
  }
  if (unit === 'days') {
    const clock = clockOf(zeroMs);
    const total = Math.round(value * 1440) + clock;
    const day = Math.floor(total / 1440);
    const rest = total - day * 1440;
    return `Tag ${day}${display.time && (rest > 0 || clock > 0) ? `, ${pad(Math.floor(rest / 60))}:${pad(rest % 60)}` : ''}`;
  }
  const year = Math.floor(value + 1e-9);
  const dayOfYear = Math.floor((value - year) * DAYS_PER_YEAR + 1e-6) + 1;
  return `Jahr ${year}${display.day && dayOfYear > 1 ? `, Tag ${dayOfYear}` : ''}`;
}

const COUNT_TEXT = /^(?:(minuten?|min\.?|stunden?|std\.?|tage?|jahre?)\s*)?([+\-\u2212]?\d+(?:[.,]\d+)?)(?:(?:\s*,\s*|\s+)(?:(\d{1,2}):(\d{2})|tag\s+(\d{1,3})|minute\s+(\d{1,2})))?$/i;

/**
 * Text einer eigenen Zählung lesen: „Tag 12, 06:00“, „Stunde 5, Minute 30“, „Jahr 340, Tag 120“, „Minute 45“ oder eine Zahl („12,5“) in
 * der Einheit `unit`. Eine Uhrzeit bei Tagen ist die echte Uhrzeit, wenn der Nullpunkt (`zeroMs`) eine hat (Umkehrung von
 * `formatCount`); ohne Uhrzeit zählt „Tag 3“ volle Tage ab dem Nullpunkt. null, wenn es keiner ist.
 */
export function parseCount(text: string, unit?: TimeUnit, zeroMs?: number | null): { unit: TimeUnit; value: number } | null {
  const m = COUNT_TEXT.exec(text.trim());
  if (!m) return null;
  const word = m[1]?.toLowerCase();
  const u: TimeUnit | undefined = word ? (word.startsWith('min') ? 'minutes' : word.startsWith('st') ? 'hours' : word.startsWith('tag') ? 'days' : 'years') : unit;
  if (!u) return null;
  let value = Number(m[2]!.replace('\u2212', '-').replace(',', '.'));
  if (!Number.isFinite(value)) return null;
  if (m[3] !== undefined) {
    // Uhrzeit nur bei Tagen
    const h = Number(m[3]);
    const mi = Number(m[4]);
    if (u !== 'days' || h > 23 || mi > 59) return null;
    value += (h * 60 + mi - clockOf(zeroMs)) / 1440;
  }
  if (m[6] !== undefined) {
    // Minute in der Stunde nur bei Stunden
    const mi = Number(m[6]);
    if (u !== 'hours' || mi > 59) return null;
    value += mi / 60;
  }
  if (m[5] !== undefined) {
    // Tag im Jahr nur bei Jahren
    const d = Number(m[5]);
    if (u !== 'years' || d < 1 || d > 366) return null;
    value += (d - 1) / DAYS_PER_YEAR;
  }
  return { unit: u, value };
}

/** Lineare Zuordnung Position ↔ Zeitwert eines Zeitstrahls (Datum: Millisekunden, eigene Zählung: Wert in ihrer Einheit). */
interface Scale {
  kind: 'date' | 'count';
  unit: TimeUnit | null;
  p0: number;
  p1: number;
  v0: number;
  v1: number;
  /** Millisekunden des Nullpunkts (eigene Zählung mit Datum) */
  zeroMs: number | null;
}

function scaleOf(t: TimelineData | null | undefined): Scale | null {
  if (!t || !(t.endPos > t.startPos)) return null;
  const r = t.reckoning;
  if (r) {
    if (!(TIME_UNITS as readonly string[]).includes(r.unit) || !Number.isFinite(r.start) || !Number.isFinite(r.end) || !(r.end > r.start)) return null;
    return { kind: 'count', unit: r.unit, p0: t.startPos, p1: t.endPos, v0: r.start, v1: r.end, zeroMs: r.zero ? parseStamp(r.zero) : null };
  }
  const a = parseStamp(t.startLabel);
  const b = parseStamp(t.endLabel);
  if (a === null || b === null || b <= a) return null;
  return { kind: 'date', unit: null, p0: t.startPos, p1: t.endPos, v0: a, v1: b, zeroMs: null };
}
const valueAt = (s: Scale, pos: number): number => s.v0 + ((pos - s.p0) / (s.p1 - s.p0)) * (s.v1 - s.v0);
const posOfValue = (s: Scale, value: number): number => s.p0 + ((value - s.v0) / (s.v1 - s.v0)) * (s.p1 - s.p0);

/** Zeit an einer Stelle des Zeitstrahls als Text (Datum `JJJJ-MM-TTThh:mm` bzw. „Tag 12, 06:00“); null ohne Zeitrechnung. */
export function timeAt(t: TimelineData, pos: number): string | null {
  const s = scaleOf(t);
  if (!s) return null;
  return s.kind === 'date' ? stampOf(valueAt(s, pos)) : formatCount(s.unit!, valueAt(s, pos), DEFAULT_TIME_DISPLAY, s.zeroMs);
}

/**
 * Umkehrung von `timeAt`: Stelle zu einer Zeit. Auf einem datierten Zeitstrahl zählt ein Datum, auf einem mit eigener Zählung ein Wert
 * der Zählung (andere Einheiten werden umgerechnet) oder – über den Nullpunkt – ein Datum. null, wenn es nicht geht.
 */
export function posAtTime(t: TimelineData, text: string): number | null {
  const s = scaleOf(t);
  if (!s) return null;
  const ms = parseStamp(text);
  if (s.kind === 'date') return ms === null ? null : posOfValue(s, ms);
  if (ms !== null) return s.zeroMs === null ? null : posOfValue(s, (ms - s.zeroMs) / 60000 / UNIT_MINUTES[s.unit!]);
  const c = parseCount(text, s.unit!, s.zeroMs);
  return c ? posOfValue(s, (c.value * UNIT_MINUTES[c.unit]) / UNIT_MINUTES[s.unit!]) : null;
}

/**
 * Vergleichsbasis eines Zeitstrahls: `ms` (Datum bzw. eigene Zählung mit Nullpunkt: Millisekunden) oder `count` (eigene Zählung ohne
 * Nullpunkt: Minuten ab einem gemeinsamen Nullpunkt); `slope` = Basiswert je Achseneinheit. null ohne Zeitrechnung.
 */
export interface TimeBasis {
  basis: 'ms' | 'count';
  at: (pos: number) => number;
  slope: number;
}

export function timeBasisOf(t: TimelineData | null | undefined): TimeBasis | null {
  const s = scaleOf(t);
  if (!s) return null;
  const slope = (s.v1 - s.v0) / (s.p1 - s.p0);
  if (s.kind === 'date') return { basis: 'ms', at: (p) => valueAt(s, p), slope };
  const f = UNIT_MINUTES[s.unit!];
  const zero = s.zeroMs;
  if (zero !== null) return { basis: 'ms', at: (p) => zero + valueAt(s, p) * f * 60000, slope: slope * f * 60000 };
  return { basis: 'count', at: (p) => valueAt(s, p) * f, slope: slope * f };
}

/** Stelle auf dem Zeitstrahl, an der ein Zeitwert der Vergleichsbasis `basis` liegt (null, wenn der Zeitstrahl eine andere hat). */
export function posAtBasis(t: TimelineData, basis: string, value: number): number | null {
  const b = timeBasisOf(t);
  return b && b.basis === basis ? t.startPos + (value - b.at(t.startPos)) / b.slope : null;
}

/** Wert eines gespeicherten Zeittexts in der Vergleichsbasis: Datum → Millisekunden, eigene Zählung (mit Einheitswort) → Minuten. */
export function timeValueOf(text: string): { basis: 'ms' | 'count'; value: number } | null {
  const ms = parseStamp(text);
  if (ms !== null) return { basis: 'ms', value: ms };
  const c = parseCount(text);
  return c ? { basis: 'count', value: c.value * UNIT_MINUTES[c.unit] } : null;
}

/** Beschriftung des Start- bzw. Ende-Fähnchens: Datum bzw. Wert der eigenen Zählung (ohne: „Start“ bzw. „Ende“). */
export function edgeLabel(t: TimelineData, edge: 'start' | 'end'): string {
  const display = timeDisplayOf(t);
  if (t.reckoning) return formatCount(t.reckoning.unit, edge === 'start' ? t.reckoning.start : t.reckoning.end, display, t.reckoning.zero ? parseStamp(t.reckoning.zero) : null);
  const label = edge === 'start' ? t.startLabel : t.endLabel;
  return label ? formatStamp(label, display) : edge === 'start' ? 'Start' : 'Ende';
}

/** Die Hauptzeitlinie: der älteste Zeitstrahl der Geschichte (es gibt immer einen; er ist nicht löschbar). */
export function mainTimelineOf(ctx: StoryCtx): StoryNode | null {
  return childrenOf(ctx, ctx.story.rootId).find((n) => n.kind === 'timeline') ?? null;
}

/** Ist der Node die Hauptzeitlinie? */
export const isMainTimeline = (ctx: StoryCtx, n: StoryNode): boolean => n.kind === 'timeline' && mainTimelineOf(ctx)?.id === n.id;

/**
 * Datum und Uhrzeit (`JJJJ-MM-TTThh:mm`) einer Achsenposition, wenn Start- und Ende-Fähnchen des Zeitstrahls Datumsangaben haben
 * (lineare Zuordnung dazwischen). Sonst null.
 */
export function dateAt(t: TimelineData, pos: number): string | null {
  const a = parseStamp(t.startLabel);
  const b = parseStamp(t.endLabel);
  if (a === null || b === null || b <= a || t.endPos <= t.startPos) return null;
  return stampOf(a + ((pos - t.startPos) / (t.endPos - t.startPos)) * (b - a));
}

/** Umkehrung von `dateAt`: Achsenposition zu einem Datum (null, wenn der Zeitstrahl keine Datumsangaben hat oder das Datum ungültig ist). */
export function posAtDate(t: TimelineData, iso: string): number | null {
  const a = parseStamp(t.startLabel);
  const b = parseStamp(t.endLabel);
  const x = parseStamp(iso);
  if (a === null || b === null || x === null || b <= a || t.endPos <= t.startPos) return null;
  return t.startPos + ((x - a) / (b - a)) * (t.endPos - t.startPos);
}

export interface NodeTimes {
  /** Die Zeit ergibt sich aus der Stelle auf dem Zeitstrahl (angedockt) statt aus den Feldern des Nodes */
  docked: boolean;
  /** Zeitpunkt bzw. Beginn (`JJJJ-MM-TTThh:mm`), null wenn unbekannt */
  start: string | null;
  /** Ende (nur Zeiträume) */
  end: string | null;
}

const asStamp = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);

/** Daten eines Zeitstrahls, wie sie gelten (früher wichen innere Zeitleisten von Zeiträumen ab; die gibt es nicht mehr). */
export function effectiveTimeline(_ctx: StoryCtx, tl: StoryNode): TimelineData | null {
  return tl.timeline;
}

/**
 * Zeit einer Szene aus ihrer Zeitangabe (`time`, PLAN.md §15). Angedockt ohne eigene Zeit (z. B. bekam der Zeitstrahl erst später eine
 * Zeitrechnung) bzw. in älteren Daten ohne sie: wie früher aus der Lage bzw. den Zeitfeldern (`legacyTimes`).
 */
export function nodeTimes(ctx: StoryCtx, n: StoryNode): NodeTimes {
  const docked = isDocked(ctx, n);
  if (!n.time || (docked && !n.time.start)) return legacyTimes(ctx, n);
  return { docked, start: n.time.start ?? null, end: n.kind === 'period' ? (n.time.end ?? null) : null };
}

/** Zeit wie vor der eigenen Zeitangabe: angedockt aus der Lage auf dem Zeitstrahl (Datum bzw. eigene Zählung), sonst aus den Zeitfeldern. */
export function legacyTimes(ctx: StoryCtx, n: StoryNode): NodeTimes {
  const timeline = n.parentId ? ctx.nodes.get(n.parentId) : undefined;
  if (timeline?.kind === 'timeline' && timeline.timeline && (n.kind === 'event' || n.kind === 'period')) {
    const t = effectiveTimeline(ctx, timeline)!;
    return n.kind === 'event'
      ? { docked: true, start: n.at === null ? null : timeAt(t, n.at), end: null }
      : { docked: true, start: n.from === null ? null : timeAt(t, n.from), end: n.to === null ? null : timeAt(t, n.to) };
  }
  if (n.kind === 'event') return { docked: false, start: asStamp(n.fields[TIME_FIELDS.event]), end: null };
  return { docked: false, start: asStamp(n.fields[TIME_FIELDS.periodStart]), end: asStamp(n.fields[TIME_FIELDS.periodEnd]) };
}

export const DEFAULT_TIME_DISPLAY: TimeDisplay = { year: true, month: true, day: true, time: true };

/** Zeitanzeige eines Zeitstrahls (fehlende Angaben: alles anzeigen). */
export const timeDisplayOf = (t: TimelineData | undefined | null): TimeDisplay => ({ ...DEFAULT_TIME_DISPLAY, ...t?.display });

/**
 * „2024-05-01T08:00“ → „01.05.2024 08:00“, begrenzt auf die Teile der Zeitanzeige (z. B. nur „05.2024“ oder „01.05.“); in einem
 * eigenen Kalender mit Namen („12. Frostmond 312 n. F. 08:00“, PF 16). Eine eigene Zählung („Tag 12, 06:00“) folgt ebenso der
 * Zeitanzeige (Uhrzeit, Tag im Jahr). Anderer Text bleibt unverändert.
 */
export function formatStamp(s: string, display: TimeDisplay = DEFAULT_TIME_DISPLAY): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(s);
  if (!m) {
    const c = /^(minute|stunde|tag|jahr)\b/i.test(s.trim()) ? parseCount(s) : null;
    return c ? formatCount(c.unit, c.value, display) : s;
  }
  if (!isGregorian()) {
    const ms = parseStamp(s);
    return ms === null ? s : calendarDateText(msToParts(ms), display, m[4] !== undefined);
  }
  const date = [display.day ? m[3] : '', display.month ? m[2] : '', display.year ? m[1] : ''].filter(Boolean);
  const text = date.join('.') + (!display.year && date.length > 1 ? '.' : '');
  const time = display.time && m[4] ? `${m[4]}:${m[5]}` : '';
  return [text, time].filter(Boolean).join(' ');
}

/** Der manuell gesetzte Text eines Fähnchens. Die Vorgabetexte früherer Versionen („Fähnchen 1“) zählen nicht als manuell. */
export const manualLabel = (label: string): string => (/^Fähnchen \d+$/.test(label.trim()) ? '' : label.trim());

/** Beschriftung eines Zeitfähnchens: der manuelle Text, sonst die Zeit an seiner Position. Ohne Zeitrechnung am Zeitstrahl: „Position n“. */
export function flagText(t: TimelineData, f: Pick<Flag, 'pos' | 'label'>): string {
  if (manualLabel(f.label) !== '') return f.label;
  const time = timeAt(t, f.pos);
  return time ? formatStamp(time, timeDisplayOf(t)) : `Position ${Math.round(f.pos)}`;
}
